import React, { useEffect, useState } from 'react';
import { downloadBidderReportPdf } from 'components/molecules/bidding_summary_bidder_report_pdf';
import AiManagementItemCard from 'components/molecules/ai_management_item_card';
import AiManagementItemsSearch from 'components/molecules/ai_management_items_search';
import AiManagementItemsSection from 'components/molecules/ai_management_items_section';
import AiManagementItemsToolbar from 'components/molecules/ai_management_items_toolbar';
import AiManagementCompetitorCompanies from 'components/molecules/ai_management_competitor_companies';
import { PossibleCompetitorsItem, usePossibleCompetitors } from 'hooks/usePossibleCompetitors';
import { toCompany } from 'hooks/usePossibleCompetitorCompanies';
import { useItemSelection } from 'hooks/useItemSelection';
import { useBidderFileGeneration } from 'hooks/useBidderFileGeneration';
import { BidderFileGeneratedHandler } from 'services/actions/ai_management_files';
import { listPossibleCompetitorCompanies } from 'services/actions/ai_management_possible_competitors';
import { money, plural } from 'services/ai_management_format';
import { PERIOD_OPTIONS } from 'services/ai_management_possible_competitors';
import { buildPossibleCompetitorsReport } from 'services/ai_management_possible_competitors_report';
import { PeriodSelect } from './styles';

interface AiManagementPossibleCompetitorsProps {
  biddingId: number | null;
  publicBodyName: string | null;
  noticeNumber: string | null;
  onFileGenerated: BidderFileGeneratedHandler;
  onOpenBidderFiles: () => void;
}

const itemMeta = (item: PossibleCompetitorsItem) =>
  [
    `${plural(item.companies, 'empresa', 'empresas')}${item.nationwide ? ' no Brasil' : ''}`,
    item.averagePrice !== null ? `Média de preço contratado: ${money(item.averagePrice)}` : null,
  ].filter((text): text is string => Boolean(text));

const AiManagementPossibleCompetitors: React.FC<AiManagementPossibleCompetitorsProps> = ({
  biddingId,
  publicBodyName,
  noticeNumber,
  onFileGenerated,
  onOpenBidderFiles,
}) => {
  const competitors = usePossibleCompetitors(biddingId);
  const { items, total, page, perPage, period, loading, error } = competitors;
  const selection = useItemSelection(items);
  const file = useBidderFileGeneration('possiveis_concorrentes', onFileGenerated);
  const [expanded, setExpanded] = useState<string | null>(null);
  const scope = competitors.state ? `no estado ${competitors.state}` : 'no estado desta licitação';
  const { clear } = selection;

  useEffect(() => {
    clear();
    setExpanded(null);
  }, [period, clear]);

  const handleGenerate = () =>
    file.generate(async () => {
      if (!biddingId) return null;
      const chosen = selection.selectedItems;
      const responses = await Promise.all(
        chosen.map((item) => listPossibleCompetitorCompanies(biddingId, item.key, period)),
      );
      const number = noticeNumber?.trim() || String(biddingId);
      return downloadBidderReportPdf(
        buildPossibleCompetitorsReport({
          items: chosen,
          companiesByKey: Object.fromEntries(
            chosen.map((item, index) => [item.key, responses[index].data.items.map(toCompany)]),
          ),
          biddingNumber: number,
          publicBody: publicBodyName ?? '',
          conlicitacaoId: String(biddingId),
          state: competitors.state,
          period,
        }),
        `Possíveis Concorrentes ${number} - Conlicitação ${biddingId}`,
      );
    });

  const emptyState = () => {
    if (items.length) return null;
    if (competitors.appliedSearch) {
      return {
        title: 'Nenhum item encontrado',
        text: `Não encontramos itens com "${competitors.appliedSearch}".`,
      };
    }
    return {
      title: 'Sem possíveis concorrentes para esta licitação',
      text: `Não encontramos licitações com objeto parecido ${scope} no período. Tente ampliar o período.`,
    };
  };

  return (
    <AiManagementItemsSection
      title="Possíveis Concorrentes"
      subtitle={`Empresas que já venceram estes itens em licitações com objeto parecido ${scope}${
        competitors.origin === 'historico'
          ? '. Esta licitação não tem a listagem de itens do edital, então os itens vêm das licitações parecidas.'
          : ''
      }${
        items.some((item) => item.nationwide)
          ? '. Itens sem resultado no estado mostram empresas do Brasil.'
          : ''
      }`}
      toolbar={
        <AiManagementItemsToolbar
          id="possible-competitors"
          shown={items.length}
          total={total}
          showActions={!loading && !error && items.length > 0}
          allSelected={selection.allSelected}
          selectedCount={selection.selectedKeys.length}
          allSelectedInterested={selection.allSelectedInterested}
          generating={file.generating}
          showAccessFile={file.showAccessFile}
          onToggleAll={selection.toggleAll}
          onToggleInterest={() =>
            competitors.toggleInterest(selection.selectedKeys, !selection.allSelectedInterested)
          }
          onGenerate={handleGenerate}
          onOpenBidderFiles={onOpenBidderFiles}
        />
      }
      search={
        <AiManagementItemsSearch
          value={competitors.search}
          showClear={Boolean(competitors.search || competitors.appliedSearch)}
          onChange={competitors.setSearch}
          onSubmit={competitors.submitSearch}
          onClear={competitors.clearSearch}
        >
          <PeriodSelect
            className="custom-select mr-4"
            aria-label="Período"
            value={period}
            onChange={(event) => competitors.setPeriod(Number(event.target.value))}
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </PeriodSelect>
        </AiManagementItemsSearch>
      }
      loading={loading}
      errorMessage={
        error ? 'Não foi possível carregar os possíveis concorrentes desta licitação.' : null
      }
      empty={emptyState()}
      page={page}
      perPage={perPage}
      total={total}
      onPageChange={competitors.setPage}
    >
      {items.map((item) => (
        <AiManagementItemCard
          key={item.key}
          id="possible-competitors-item"
          label={item.label}
          meta={itemMeta(item)}
          title={item.description ?? item.catalog ?? undefined}
          interested={item.interested}
          selected={selection.isSelected(item.key)}
          expanded={expanded === item.key}
          canExpand={item.companies > 0}
          onToggleSelected={() => selection.toggle(item)}
          onToggleExpanded={() => setExpanded(expanded === item.key ? null : item.key)}
          onToggleInterest={() => competitors.toggleInterest([item.key], !item.interested)}
        >
          <AiManagementCompetitorCompanies
            biddingId={biddingId}
            itemKey={item.key}
            itemLabel={item.label}
            period={period}
          />
        </AiManagementItemCard>
      ))}
    </AiManagementItemsSection>
  );
};

export default AiManagementPossibleCompetitors;
